/**
 * Stage value resolution utilities for proposal action cards.
 *
 * Proposal payloads may store stage values as either:
 *   - entity_ids (UUIDs) → resolved directly by useStages
 *   - stage_names (e.g. "ahoy_negotiation") → need name-based lookup
 *
 * These helpers resolve both formats to human-readable labels for display,
 * and to entity_ids for the stage selector input.
 */

import type { Stage } from '@/lib/crm/hooks/useStages';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns true if the string is a well-formed UUID (version-agnostic).
 * Used to distinguish stage entity_ids from legacy stage names.
 */
export function isUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Resolve a raw stage value (entity_id or stage_name) to a Stage object.
 *
 * Lookup precedence:
 *   1. Exact entity_id match
 *   2. Label match (case-insensitive, with ahoy_ prefix stripped)
 *   3. Label match against underscore-to-space converted value
 */
export function resolveStage(
  rawValue: string,
  stages: Stage[]
): Stage | undefined {
  // 1. Try direct entity_id lookup (fast path for UUIDs)
  const byId = stages.find(s => s.entity_id === rawValue);
  if (byId) {
    return byId;
  }

  // Normalize the raw value for name-based matching
  const normalized = rawValue.replace(/^ahoy_/, '').toLowerCase();
  const normalizedSpaced = normalized.replace(/_/g, ' ');

  // 2. Try label match (case-insensitive)
  const byLabel = stages.find(
    s =>
      s.label.toLowerCase() === normalized ||
      s.label.toLowerCase() === normalizedSpaced
  );
  if (byLabel) {
    return byLabel;
  }

  return undefined;
}

/**
 * Resolve a raw stage value to its display label.
 * Falls back to formatting the raw value as a title if no stage match is found.
 */
export function resolveStageLabel(rawValue: string, stages: Stage[]): string {
  const stage = resolveStage(rawValue, stages);
  if (stage) {
    return stage.label;
  }

  // Fallback: format the raw value as a readable title
  return rawValue
    .replace(/^ahoy_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Resolve a raw stage value to its entity_id.
 * Returns the original value if no stage match is found (it may already be a UUID).
 */
export function resolveStageEntityId(
  rawValue: string,
  stages: Stage[]
): string {
  const stage = resolveStage(rawValue, stages);
  return stage?.entity_id ?? rawValue;
}
